import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, X } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { useIsBelowDesktop } from "@/hooks/use-mobile";

const NEWSLETTER_DISMISS_KEY = "elevest:newsletter-sticky:startseite:dismissed";

/**
 * Sticky CTA for the homepage.
 * - Appears once the Hero section has left the viewport
 * - Hides while the LeadForm (#lead) or Footer is in view to avoid duplicate CTAs
 * - Below desktop (<1024px) renders as a bottom bar. If the Newsletter CTA was
 *   not dismissed in this session, the bar consistently splits into a 2-column
 *   grid: Strategy (primary) + Architektur-Brief (outline).
 */
const HomeStickyCTA = () => {
  const { lang } = useLanguage();
  const isBelowDesktop = useIsBelowDesktop();
  const [visible, setVisible] = useState(false);

  // Newsletter sticky companion state (only relevant below desktop — desktop
  // renders <NewsletterStickyBar /> as its own sidebar card with the same key).
  const [newsletterDismissed, setNewsletterDismissed] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    setNewsletterDismissed(sessionStorage.getItem(NEWSLETTER_DISMISS_KEY) === "1");
  }, []);

  useEffect(() => {
    const hero = document.getElementById("hero");
    if (!hero) return;

    let heroInView = true;
    let leadInView = false;
    let footerInView = false;
    let observedLead: HTMLElement | null = null;
    let observedFooter: HTMLElement | null = null;

    const update = () => {
      setVisible(!heroInView && !leadInView && !footerInView);
    };

    const heroObs = new IntersectionObserver(
      ([entry]) => { heroInView = entry.isIntersecting; update(); },
      { threshold: 0 }
    );
    const leadObs = new IntersectionObserver(
      ([entry]) => { leadInView = entry.isIntersecting; update(); },
      { threshold: 0, rootMargin: "100px 0px 0px 0px" }
    );
    const footerObs = new IntersectionObserver(
      ([entry]) => { footerInView = entry.isIntersecting; update(); },
      { threshold: 0, rootMargin: "100px 0px 0px 0px" }
    );

    const attachDynamicTargets = () => {
      const lead = document.getElementById("lead");
      const footer = document.querySelector("footer");

      if (lead && lead !== observedLead) {
        if (observedLead) leadObs.unobserve(observedLead);
        observedLead = lead;
        leadObs.observe(lead);
      }

      if (footer instanceof HTMLElement && footer !== observedFooter) {
        if (observedFooter) footerObs.unobserve(observedFooter);
        observedFooter = footer;
        footerObs.observe(footer);
      }

      update();
    };

    heroObs.observe(hero);
    attachDynamicTargets();

    const targetMountObs = new MutationObserver(attachDynamicTargets);
    targetMountObs.observe(document.body, { childList: true, subtree: true });

    return () => {
      targetMountObs.disconnect();
      heroObs.disconnect();
      leadObs.disconnect();
      footerObs.disconnect();
    };
  }, []);

  const label = lang === "de" ? "Kostenlose Strategieberatung" : "Free Strategy Consultation";
  const button = lang === "de" ? "Termin vereinbaren" : "Book a meeting";
  const newsletterLabel = lang === "de" ? "Architektur-Brief" : "Architecture Brief";
  const newsletterButton = lang === "de" ? "Anmelden" : "Subscribe";

  const handleNewsletterClick = () => {
    const el = document.getElementById("newsletter");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleNewsletterDismiss = () => {
    setNewsletterDismissed(true);
    if (typeof window !== "undefined") {
      sessionStorage.setItem(NEWSLETTER_DISMISS_KEY, "1");
    }
  };

  if (!isBelowDesktop) {
    return (
      <AnimatePresence>
        {visible && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            transition={{ duration: 0.3, ease: [0.21, 0.47, 0.32, 0.98] }}
            className="fixed right-6 xl:right-10 top-1/3 z-40 w-52"
          >
            <div className="bg-primary text-primary-foreground rounded-xl p-5 shadow-lg">
              <p className="text-xs uppercase tracking-wider font-semibold text-primary-foreground/60 mb-2">
                Elevest Partners
              </p>
              <p className="text-sm font-medium leading-snug mb-4">{label}</p>
              <Link
                to="/#lead"
                className="inline-flex items-center gap-1.5 w-full justify-center px-4 py-2.5 bg-background text-foreground rounded-lg text-xs font-semibold hover:bg-background/90 transition-colors"
              >
                {button} <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    );
  }

  const showNewsletter = !newsletterDismissed;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.25, ease: [0.21, 0.47, 0.32, 0.98] }}
          className="fixed bottom-0 left-0 right-0 z-40 px-4 pb-[env(safe-area-inset-bottom,8px)] pt-2 bg-background/80 backdrop-blur-lg border-t border-border"
        >
          {showNewsletter ? (
            <div className="grid grid-cols-2 gap-2 relative">
              <Link
                to="/#lead"
                className="flex items-center justify-center gap-1.5 py-3 bg-primary text-primary-foreground rounded-lg text-xs font-semibold hover:bg-primary/90 transition-colors"
              >
                {button} <ArrowRight className="w-3.5 h-3.5" />
              </Link>
              <button
                type="button"
                onClick={handleNewsletterClick}
                className="relative flex items-center justify-center gap-1.5 py-3 bg-background text-foreground border border-border rounded-lg text-xs font-semibold hover:bg-muted transition-colors"
                aria-label={newsletterLabel}
              >
                {newsletterButton} <ArrowRight className="w-3.5 h-3.5" />
                <span
                  role="button"
                  tabIndex={0}
                  onClick={(e) => { e.stopPropagation(); handleNewsletterDismiss(); }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.stopPropagation();
                      handleNewsletterDismiss();
                    }
                  }}
                  aria-label="Dismiss"
                  className="absolute -top-1.5 -right-1.5 w-5 h-5 inline-flex items-center justify-center rounded-full bg-background border border-border text-muted-foreground hover:text-foreground shadow-sm cursor-pointer"
                >
                  <X size={11} />
                </span>
              </button>
            </div>
          ) : (
            <Link
              to="/#lead"
              className="flex items-center justify-center gap-2 w-full py-3 bg-primary text-primary-foreground rounded-lg text-sm font-semibold hover:bg-primary/90 transition-colors"
            >
              {button} <ArrowRight className="w-4 h-4" />
            </Link>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default HomeStickyCTA;
